import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FORMLY_FIELD_TYPES } from '@app/constants';
import { FeedbackService, ReportService } from '@app/core/services';
import { CellTransform, ColumnFilter, TableConfig } from '@app/interfaces';
import { Filter, FilterConfig } from '@app/interfaces/filter';
import { ChangeHistory, TableChangeHistory } from '@app/models/change-history.model';
import { mapArrayToSelectOptions } from '@app/utils';
import * as Filters from '@app/utils/filters';
import * as Transforms from '@app/utils/transforms';
import { BaseListComponent } from '../base-list.component';
import { ChangeHistoryService } from './services/change-history.service';
import { FiltersComponent } from '../../forms/components/filters/filters.component';
import { ModalComponent } from '../modal/modal.component';
import { TableComponent } from '../table/table.component';

@Component({
	selector: 'sl-change-history',
	templateUrl: './change-history.component.html',
	standalone: true,
	imports: [ModalComponent, FiltersComponent, TableComponent],
})
export class ChangeHistoryComponent extends BaseListComponent<TableChangeHistory, ChangeHistoryService> implements OnInit {
	disableSavedSettings = true;
	ignoreGetList = true;
	entityName: string;
	listingPagePath = '';

	config: TableConfig<TableChangeHistory> = {
		...this.config,
		columns: [
			{
				header: 'Changed By',
				key: 'ChangeBy',
			},
			{
				header: 'Change Date',
				key: 'ChangeDate',
				transform: ({ value }: CellTransform<TableChangeHistory>): string => Transforms.toDateFormat(value, 'MM/DD/YYYY hh:mm:ss A'),
				filter: ({ row, filterValue }: ColumnFilter<TableChangeHistory>): boolean =>
					Filters.dateInRange(row.ChangeDate, { start: filterValue.start, end: filterValue.end, granularity: 'day', canMatch: true }),
			},
			{
				header: 'Description',
				key: 'Description',
			},
			{
				header: 'Previous Value',
				key: 'OldValue',
			},
			{
				header: 'Changed To',
				key: 'NewValue',
			},
		],
		expandedColumns: [
			{
				header: 'Field',
				key: 'Description',
			},
			{
				header: 'Previous Value',
				key: 'OldValue',
			},
			{
				header: 'Changed To',
				key: 'NewValue',
			},
		],
		expandedColumnName: 'SubChanges',
		onRowClick: null,
		showRowActions: false,
		defaultSortDirection: 'desc',
		defaultSortField: 'ChangeDate',
	};

	filterFields: FormlyFieldConfig[] = [
		{
			fieldGroupClassName: 'row',
			fieldGroup: [
				{
					key: 'Description',
					className: 'col-xs-12',
					type: FORMLY_FIELD_TYPES.select,
					templateOptions: {
						label: 'Description',
						options: [],
					},
					expressionProperties: {
						'templateOptions.options': 'formState.selects.descriptionOptions',
					},
				},
			],
		},
		{
			fieldGroupClassName: 'row',
			fieldGroup: [
				{
					key: 'ChangeBy',
					className: 'col-xs-6',
					type: FORMLY_FIELD_TYPES.select,
					templateOptions: {
						label: 'Change By',
						options: [],
					},
					expressionProperties: {
						'templateOptions.options': 'formState.selects.changeByOptions',
					},
				},
				{
					key: 'ChangeDate',
					className: 'col-xs-6',
					type: FORMLY_FIELD_TYPES.datepickerRange,
					templateOptions: {
						label: 'Change Date',
					},
				},
			],
		},
	];

	filterOptions: FormlyFormOptions = {
		formState: {
			selects: {
				descriptionOptions: [],
				changeByOptions: [],
			},
		},
	};

	filterConfig: FilterConfig<TableChangeHistory>[] = [
		{
			key: 'ChangeDate',
			transform: (filter: Filter): string => Transforms.toDateRange({ start: filter.value.start, end: filter.value.end }),
		},
	];

	constructor(
		protected readonly listService: ChangeHistoryService,
		protected readonly dialog: MatDialog,
		protected readonly feedbackService: FeedbackService,
		protected readonly route: ActivatedRoute,
		protected readonly router: Router,
		protected readonly reportService: ReportService,
		@Inject(MAT_DIALOG_DATA) dialogData: { entity: string; id: number }
	) {
		super(dialog, feedbackService, route, router, reportService);
		this.listService.entity = dialogData.entity;
		this.listService.id = dialogData.id;
	}

	afterOnInit(): void {
		//Hack added to prevent "Expression has changed after it was checked." error with loading component.
		//Only occurs with Modals, seems like an Angular bug
		setTimeout(() => {
			this.getChangeHistory();
		});
	}

	getChangeHistory(): void {
		this.listService.getList().subscribe((changeHistories: ChangeHistory[]) => {
			const flattenedData = this.transformChangeHistory(changeHistories);
			this.data = flattenedData;
			this.data$.next(this.data);

			this.filterOptions.formState.selects = {
				descriptionOptions: mapArrayToSelectOptions(this.data, { value: 'Description' }),
				changeByOptions: mapArrayToSelectOptions(this.data, { value: 'ChangeBy' }),
			};
		});
	}

	transformChangeHistory(changeHistories: ChangeHistory[]): TableChangeHistory[] {
		const histories = changeHistories.map((changeHistory) =>
			changeHistory.Changes.map(
				(change) =>
					({
						ChangeBy: changeHistory.ChangeBy,
						ChangeDate: changeHistory.ChangeDate,
						Label: change.Label,
						Description: change.Description,
						OldValue: change.OldValue,
						NewValue: change.NewValue,
						SubChanges: change.SubChanges.length > 0 ? change.SubChanges : null,
					} as TableChangeHistory)
			)
		);
		return histories.flat();
	}
}
